import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';

import localization from '../../../../shared/strings';
import Inventory from '../../Inventory/Inventory';
import { INVENTORY_PRESETS } from '../../helpers/productDetailsPresets';
import { aggregatedInventoryFieldsPropType } from '../helpers/aggregatedFieldPropTypes';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import {
  getAggregatedCommonBoolean,
  getAggregatedCommonString,
  isAggregatedIdentityFieldEditable,
} from '../helpers/aggregatedFieldHelpers';

const l10n = localization.PRODUCT_INFO;

const AggregatedInventorySection = ({
  inventoryFields,
  readOnly,
  readOnlyTooltip,
  skuLabel,
  showTracked,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
}) => {
  const skuBindings = useBindAggregatedField('sku', inventoryFields?.sku, skuLabel || l10n.autoMappedFieldLabels.sku);
  const barcodeBindings = useBindAggregatedField(
    'barcode',
    inventoryFields?.barcode,
    l10n.autoMappedFieldLabels.barcode,
  );
  const trackedBindings = useBindAggregatedField('tracked', inventoryFields?.tracked, 'Track quantity');

  const onChangeSku = useCallback((sku) => {
    skuBindings.onChange(sku);
  }, [skuBindings]);

  const onChangeBarcode = useCallback((barcode) => {
    barcodeBindings.onChange(barcode);
  }, [barcodeBindings]);

  const onChangeTracked = useCallback((tracked) => {
    trackedBindings.onChange(tracked);
  }, [trackedBindings]);

  if (!inventoryFields) return null;

  const skuReadOnly = !isAggregatedIdentityFieldEditable(inventoryFields.sku);
  const barcodeReadOnly = !isAggregatedIdentityFieldEditable(inventoryFields.barcode);

  return (
    <Inventory
      {...INVENTORY_PRESETS.skuCodes}
      aggregatedFields={inventoryFields}
      hideTracked={!showTracked}
      tracked={getAggregatedCommonBoolean(inventoryFields.tracked, false)}
      sku={getAggregatedCommonString(inventoryFields.sku, '')}
      barcode={getAggregatedCommonString(inventoryFields.barcode, '')}
      skuLabel={skuLabel || l10n.autoMappedFieldLabels.sku}
      readOnly={readOnly}
      skuReadOnly={skuReadOnly}
      barcodeReadOnly={barcodeReadOnly}
      readOnlyTooltip={readOnlyTooltip}
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle || l10n.skuCodesBlockTitle}
      onChangeSku={onChangeSku}
      onChangeBarcode={onChangeBarcode}
      onChangeTracked={onChangeTracked}
      onChangeInventoryPolicy={() => null}
    />
  );
};

AggregatedInventorySection.propTypes = {
  inventoryFields: aggregatedInventoryFieldsPropType,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  skuLabel: PropTypes.string,
  showTracked: PropTypes.bool,
  inlineSkuBarcode: PropTypes.bool,
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node,
};

AggregatedInventorySection.defaultProps = {
  inventoryFields: null,
  readOnly: false,
  readOnlyTooltip: null,
  skuLabel: null,
  showTracked: false,
  inlineSkuBarcode: false,
  blockTitle: null,
};

export default memo(AggregatedInventorySection);
